/**
 * Shared training edit-dialog hydration helpers (trng004 / trng016 / trng028).
 * Preserve stored lecturer/company_contact/machines during initial open while
 * cascade option lists may still be empty/incomplete. User-driven dependency
 * changes continue to prune via normal cascade.
 *
 * `selectedCard` / row args accept any training-shaped row (card, invitation,
 * state-list selection) with lecturer_id / company_contact / machines.
 */

import { normalizeMachineIds, normalizePositiveId } from './trainingSelectionCascade';

export function resolveTrainingCardEditLecturerId(row) {
  return normalizePositiveId(
    row?.lecturer_id ?? row?.company_contact?.lecturer_id ?? row?.lecturer?.id
  );
}

export function resolveTrainingCardEditCompanyContactId(row) {
  return normalizePositiveId(row?.company_contact_id ?? row?.company_contact?.id);
}

export function resolveTrainingCardEditCompanyId(row) {
  return normalizePositiveId(
    row?.company_id ?? row?.company?.id ?? row?.training?.training_company_id
  );
}

export function buildTrainingCardEditFormValues(row) {
  if (!row || typeof row !== 'object') {
    return {
      training_id: '',
      initial_date: '',
      deactivation_date: '',
      description: '',
      company_id: '',
      machines: [],
      lecturer_id: '',
      company_contact_id: '',
    };
  }
  const lecturerId = resolveTrainingCardEditLecturerId(row);
  const contactId = resolveTrainingCardEditCompanyContactId(row);
  const companyId = resolveTrainingCardEditCompanyId(row);
  return {
    ...row,
    company_id: companyId ?? '',
    machines: (row.machines || []).map((m) => (
      m && typeof m === 'object' && m.id != null ? { name: m.name, id: m.id } : m
    )),
    lecturer_id: lecturerId ?? '',
    company_contact_id: contactId ?? '',
  };
}

export function machineIdsKey(machines) {
  return normalizeMachineIds(machines).slice().sort((a, b) => a - b).join(',');
}

/**
 * When preserveHydrated is true, keep current form IDs if cascade returned blanks
 * (typical while companyContactsList/lecturers are not yet usable).
 */
export function mergeTrainingCardCascadeFormValues(currentValues, cascadeValues, { preserveHydrated } = {}) {
  const next = { ...(cascadeValues || {}) };
  if (!preserveHydrated) return next;

  const curL = currentValues?.lecturer_id;
  const curC = currentValues?.company_contact_id;
  const curM = currentValues?.machines;
  const curCompany = currentValues?.company_id;

  if ((next.lecturer_id === '' || next.lecturer_id == null) && curL !== '' && curL != null) {
    next.lecturer_id = curL;
  }
  if ((next.company_contact_id === '' || next.company_contact_id == null) && curC !== '' && curC != null) {
    next.company_contact_id = curC;
  }
  if ((!Array.isArray(next.machines) || next.machines.length === 0)
    && Array.isArray(curM) && curM.length > 0) {
    next.machines = curM;
  }
  if ((next.company_id === '' || next.company_id == null) && curCompany !== '' && curCompany != null) {
    next.company_id = curCompany;
  }
  return next;
}

/**
 * Build lecturer options for edit dialog: cascade-filtered list plus stored row lecturer.
 */
export function buildEditFormLecturerOptions({
  cascadeLecturers = [],
  companyContactsList = [],
  fullLecturers = [],
  selectedCard = null,
} = {}) {
  if (!selectedCard) return cascadeLecturers || [];
  const companyId = resolveTrainingCardEditCompanyId(selectedCard);
  const machineIds = normalizeMachineIds(selectedCard?.machines);
  const contactLecturerIds = (companyContactsList || [])
    .filter((contact) => (
      companyId != null
      && Number(contact?.company_id) === Number(companyId)
      && (machineIds.length === 0 || machineIds.every((id) => (
        (contact.machines || []).some((mch) => Number(mch?.id ?? mch) === Number(id))
      )))
    ))
    .map((contact) => contact.lecturer_id);
  const filtered = (fullLecturers || []).filter((lecturer) => (
    contactLecturerIds.some((id) => Number(id) === Number(lecturer?.id))
  ));
  const base = filtered.length ? filtered : (cascadeLecturers || []);
  const rowLecturerId = resolveTrainingCardEditLecturerId(selectedCard);
  if (rowLecturerId == null) return base;
  if (base.some((l) => Number(l?.id) === Number(rowLecturerId))) return base;
  const fromCatalog = (fullLecturers || []).find((l) => Number(l?.id) === Number(rowLecturerId));
  const rowLecturer = selectedCard?.lecturer ?? selectedCard?.company_contact?.lecturer;
  const fallback = fromCatalog || (
    rowLecturer && typeof rowLecturer === 'object'
      ? { id: rowLecturer.id ?? rowLecturerId, name: rowLecturer.name || `Lektor #${rowLecturerId}` }
      : { id: rowLecturerId, name: `Lektor #${rowLecturerId}` }
  );
  return [fallback, ...base];
}

/**
 * Build trainer options for edit dialog: cascade-filtered list plus stored company_contact.
 */
export function buildEditFormCompanyContactOptions({
  cascadeContacts = [],
  companyContactsList = [],
  selectedCard = null,
} = {}) {
  if (!selectedCard) return cascadeContacts || [];
  const companyId = resolveTrainingCardEditCompanyId(selectedCard);
  const machineIds = normalizeMachineIds(selectedCard?.machines);
  const lecturerId = resolveTrainingCardEditLecturerId(selectedCard);
  const filtered = (companyContactsList || []).filter((contact) => (
    companyId != null
    && Number(contact?.company_id) === Number(companyId)
    && (machineIds.length === 0 || machineIds.every((id) => (
      (contact.machines || []).some((mch) => Number(mch?.id ?? mch) === Number(id))
    )))
    && (lecturerId == null || Number(contact?.lecturer_id) === Number(lecturerId))
  ));
  const base = filtered.length ? filtered : (cascadeContacts || []);
  const rowContactId = resolveTrainingCardEditCompanyContactId(selectedCard);
  if (rowContactId == null) return base;
  if (base.some((c) => Number(c?.id) === Number(rowContactId))) return base;
  const rowContact = selectedCard?.company_contact;
  const fromList = (companyContactsList || []).find((c) => Number(c?.id) === Number(rowContactId));
  if (fromList) return [fromList, ...base];
  if (!rowContact) {
    return [{ id: rowContactId, name: `Kontakt #${rowContactId}` }, ...base];
  }
  return [{
    id: rowContact.id ?? rowContactId,
    name: rowContact?.employee?.name ?? rowContact?.name ?? `Kontakt #${rowContactId}`,
    employee: rowContact?.employee,
    company_id: rowContact?.company_id,
    lecturer_id: rowContact?.lecturer_id,
  }, ...base];
}
